// DEALS-CREATION-FLOW-B2 — Property cooperation offer helpers.
// Nested API key: cooperation_offer = {enabled, type, value, currency, terms}.

export const COOPERATION_CURRENCY_OPTIONS = [
  { value: "USD", label: "USD — Долар" },
  { value: "EUR", label: "EUR — Євро" },
  { value: "UAH", label: "UAH — Гривня" },
];

export const emptyCooperationFormFields = () => ({
  cooperation_enabled: false,
  cooperation_type: "",
  cooperation_value: "",
  cooperation_currency: "USD",
  cooperation_terms: "",
});

export const hydrateCooperationFormFields = (offer) => {
  if (!offer || typeof offer !== "object") {
    return emptyCooperationFormFields();
  }
  return {
    cooperation_enabled: Boolean(offer.enabled),
    cooperation_type: offer.type || "",
    cooperation_value:
      offer.value === null || offer.value === undefined ? "" : String(offer.value),
    cooperation_currency: offer.currency || "USD",
    cooperation_terms: offer.terms || "",
  };
};

/**
 * Build nested cooperation_offer for Property create/update.
 * When disabled, omit financial fields so stale percent/fixed values
 * cannot ride along as an active payload.
 */
export const buildCooperationOfferPayload = (core) => {
  const enabled = Boolean(core && core.cooperation_enabled);
  if (!enabled) {
    const payload = { enabled: false };
    if (core && Object.prototype.hasOwnProperty.call(core, "cooperation_terms")) {
      payload.terms = core.cooperation_terms || "";
    }
    return payload;
  }

  const type = core.cooperation_type || "";
  const payload = {
    enabled: true,
    type,
    value: core.cooperation_value === "" ? null : core.cooperation_value,
    terms: core.cooperation_terms || "",
  };

  if (type === "fixed") {
    payload.currency = core.cooperation_currency || "";
  } else {
    payload.currency = null;
  }

  return payload;
};

export const mergeCooperationOfferFieldErrors = (error, base = {}) => {
  const data = error && error.response && error.response.data;
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return base;
  }

  const next = { ...base };
  const offer = data.cooperation_offer;

  const pickFirstString = (value) => {
    if (typeof value === "string") return value;
    if (Array.isArray(value) && value.length > 0) {
      return typeof value[0] === "string" ? value[0] : String(value[0]);
    }
    return undefined;
  };

  if (typeof offer === "string") {
    next.cooperation_enabled = offer;
    return next;
  }

  if (offer && typeof offer === "object" && !Array.isArray(offer)) {
    const map = {
      enabled: "cooperation_enabled",
      type: "cooperation_type",
      value: "cooperation_value",
      currency: "cooperation_currency",
      terms: "cooperation_terms",
    };
    for (const [apiKey, formKey] of Object.entries(map)) {
      const msg = pickFirstString(offer[apiKey]);
      if (msg) next[formKey] = msg;
    }
    const nestedForm = pickFirstString(offer.non_field_errors);
    if (nestedForm) next.cooperation_enabled = nestedForm;
  }

  return next;
};

export const hasVisibleCooperationOffer = (offer) =>
  Boolean(offer && typeof offer === "object" && offer.enabled);

export const estimatePercentReward = (offer, propertyPrice) => {
  if (!offer || offer.type !== "percent") return null;
  const percent = Number(offer.value);
  const price = Number(propertyPrice);
  if (!Number.isFinite(percent) || percent <= 0) return null;
  if (!Number.isFinite(price) || price <= 0) return null;
  // Match backend Decimal HALF_UP to 2 places for display estimate.
  const raw = (price * percent) / 100;
  return Math.round((raw + Number.EPSILON) * 100) / 100;
};

export const formatCooperationBadgeText = (offer, property) => {
  if (!hasVisibleCooperationOffer(offer)) return null;

  if (offer.type === "percent") {
    const percent = Number(offer.value);
    if (!Number.isFinite(percent)) return null;
    const pctLabel = `${percent.toLocaleString("uk-UA", {
      maximumFractionDigits: 2,
    })}%`;
    const estimate = estimatePercentReward(offer, property && property.price);
    if (
      estimate != null &&
      property &&
      property.currency
    ) {
      const formatted = estimate
        .toLocaleString("uk-UA", { maximumFractionDigits: 2 })
        .replace(/\u00a0/g, " ");
      return `Винагорода партнеру: ${pctLabel} (~${formatted} ${property.currency})`;
    }
    return `Винагорода партнеру: ${pctLabel}`;
  }

  if (offer.type === "fixed") {
    const amount = Number(offer.value);
    if (!Number.isFinite(amount)) return null;
    const formatted = amount
      .toLocaleString("uk-UA", { maximumFractionDigits: 2 })
      .replace(/\u00a0/g, " ");
    const currency = offer.currency || "";
    return `Винагорода партнеру: ${formatted}${currency ? ` ${currency}` : ""}`;
  }

  return null;
};

export const computeFrozenRewardPreview = ({
  offer,
  dealPrice,
  dealCurrency,
}) => {
  if (!hasVisibleCooperationOffer(offer)) {
    return { ok: false, reason: "offer_missing" };
  }

  if (offer.type === "percent") {
    const amount = estimatePercentReward(offer, dealPrice);
    if (amount == null) {
      return { ok: false, reason: "invalid_percent" };
    }
    return {
      ok: true,
      type: "percent",
      percent: Number(offer.value),
      amount,
      currency: dealCurrency,
    };
  }

  if (offer.type === "fixed") {
    if (offer.currency && dealCurrency && offer.currency !== dealCurrency) {
      return {
        ok: false,
        reason: "currency_mismatch",
        offerCurrency: offer.currency,
        dealCurrency,
      };
    }
    const amount = Number(offer.value);
    if (!Number.isFinite(amount) || amount <= 0) {
      return { ok: false, reason: "invalid_fixed" };
    }
    return {
      ok: true,
      type: "fixed",
      amount,
      currency: offer.currency || dealCurrency,
    };
  }

  return { ok: false, reason: "unknown_type" };
};
